import * as Yup from 'yup';

import { yesNo } from '@FormModels/shared';
import { containsAnySubStr, isEqualStr } from '@Shared/helpers/string';
import { normalizeNumber } from '@src/shared/helpers';
import { parseApiDate, toApiDate, years } from '@src/shared/helpers/date';

import { requiredErrorMessage } from './applicantDetails';

export const numberOfBedroomsErrorMessage =
    'This a required field. For a studio enter 0';

export const minimumPropertyWorth = 100000;

const isFlat = (value: string) =>
    containsAnySubStr(value, ['flat', 'maisonette']);
const wasConstructedAfter2010 = (value: string) =>
    isEqualStr(value, 'After 2010');

const booleanFromRequest = (value: boolean | null) =>
    value === null || value === undefined ? null : value ? 'true' : 'false';

export const isHelpToBuyNotRepaid = (
    ownershipSchemeType?: string | null,
    ownershipSchemeRepaid?: string | boolean | null,
) =>
    ownershipSchemeType === 'Help to buy' &&
    String(ownershipSchemeRepaid) === 'false';

const htbConditionFields = ['ownershipSchemeType', 'ownershipSchemeRepaid'];

const htbToRequest =
    (toRequest = (value: any, _propertyDetails: any): any => value) =>
    (value: any, propertyDetails: any) =>
        isHelpToBuyNotRepaid(
            propertyDetails.ownershipSchemeType,
            propertyDetails.ownershipSchemeRepaid,
        )
            ? toRequest(value, propertyDetails)
            : null;

const hasHtbStructuralAlterations = (
    hasAlterations: string,
    ownershipSchemeType: string,
    ownershipSchemeRepaid: string,
) =>
    hasAlterations === 'true' &&
    isHelpToBuyNotRepaid(ownershipSchemeType, ownershipSchemeRepaid);

export const htbFieldNames = [
    'helpToBuyEquityLoanAccountNumber',
    'hasPropertyValueAffectingFactors',
    'impactToPropertyValue',
    'impactToPropertyValueOther',
    'hasStructuralAlterationsToProperty',
    'structuralAlterationsApprovedByHomesEngland',
    'structuralAlterationsDetails',
];

const hasOwnershipScheme = (propertyDetails: any) =>
    String(propertyDetails?.hasSharedOwnership) === 'true';

export const isOtherOwnershipSchemeValue = (value: string) =>
    isEqualStr(value, 'Other');

export const constructionYearOptions = [
    { label: 'Before 1914', value: 'Before 1914' },
    { label: '1914 - 2000', value: '1914 - 2000' },
    { label: '2000 - 2010', value: '2000 - 2010' },
    { label: 'After 2010', value: 'After 2010' },
];

export const warrantyCertificateOptions = [
    { label: 'ABC+', value: 'ABC+' },
    { label: 'Advantage', value: 'Advantage' },
    { label: 'AEDIS Warranties Ltd', value: 'AEDIS Warranties Ltd' },
    { label: 'ARK', value: 'ARK' },
    {
        label: 'Build Assure (New Homes Structural Defects Insurance)',
        value: 'Build Assure (New Homes Structural Defects Insurance)',
    },
    { label: 'Building Life Plan', value: 'Building Life Plan' },
    {
        label: 'Build-Zone Structural Warranty',
        value: 'Build-Zone Structural Warranty',
    },
    {
        label: 'Checkmate Castle 10 New Home',
        value: 'Checkmate Castle 10 New Home',
    },
    {
        label: 'Global Home Warranties 10 Year Structural Defects Insurance',
        value: 'Global Home Warranties 10 Year Structural Defects Insurance',
    },
    { label: 'HCI and Protek', value: 'HCI and Protek' },
    {
        label: 'ICW (International Construction Warranties)',
        value: 'ICW (International Construction Warranties)',
    },
    { label: 'LABC New Home Warranty', value: 'LABC New Home Warranty' },
    { label: 'NHBC', value: 'NHBC' },
    { label: 'None', value: 'None' },
    { label: 'One Guarantee', value: 'One Guarantee' },
    { label: 'Other', value: 'Other' },
    { label: 'Premier Guarantee', value: 'Premier Guarantee' },
    { label: 'Q Policy', value: 'Q Policy' },
];
export const ownershipSchemeTypeOptions = [
    { label: 'Shared Ownership', value: 'Shared Ownership' },
    { label: 'Help to Buy', value: 'Help to buy' },
    { label: 'Right to Buy', value: 'Right to buy' },
    { label: 'Other', value: 'Other' },
];
export const otherOwnershipSchemeTypeOptions = [
    { label: 'New Build HomeBuy', value: 'New Build HomeBuy' },
    { label: 'FirstBuy', value: 'FirstBuy' },
    { label: 'First Homes Scheme', value: 'First Homes Scheme' },
    { label: 'London Living Rent', value: 'London Living Rent' },
    { label: 'Rent to Buy', value: 'Rent to Buy' },
    { label: 'Other', value: 'Other' },
];

export const impactToPropertyValueOptions = [
    { label: 'External cladding', value: 'External cladding', checked: false },
    {
        label: 'Structural defects',
        value: 'Structural defects',
        checked: false,
    },
    {
        label: 'Planning permission and/or building regulation breaches',
        value: 'Planning permission and/or building regulation breaches',
        checked: false,
    },
    {
        label: 'No building warranty',
        value: 'No building warranty',
        checked: false,
    },
    { label: 'Subsidence', value: 'Subsidence', checked: false },
    {
        label: 'Impacted by HS2 line',
        value: 'Impacted by HS2 line',
        checked: false,
    },
    { label: 'Other', value: 'Other', checked: false },
];

export const externalCladdingHardStopMessage =
    "We can't continue with your application if your property has external cladding.";

export const formItems = {
    whenHasLastPurchased: {
        items: years,
        validation: Yup.string().required(requiredErrorMessage),
        transform: {
            target: 'whenHasLastPurchased',
            toRequest: (value: string | number) => {
                if ((typeof value === 'number' && isNaN(value)) || !value) {
                    return '';
                }
                return toApiDate(`01/01/${value}`);
            },
            fromRequest: (value: string) => {
                try {
                    return value
                        ? parseApiDate(value).getFullYear()
                        : undefined;
                } catch {
                    // date was invalid
                    return undefined;
                }
            },
        },
    },
    hasSharedOwnership: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: { target: 'hasSharedOwnership' },
    },
    ownershipSchemeType: {
        items: ownershipSchemeTypeOptions,
        default: ownershipSchemeTypeOptions,
        validation: Yup.string()
            .nullable()
            .when('hasSharedOwnership', {
                is: 'true',
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),

        transform: {
            target: 'ownershipSchemeType',
            toRequest: (
                value: string,
                ownershipTypeDetails: {
                    hasSharedOwnership: boolean | string;
                    ownershipSchemeType: string;
                    otherOwnershipSchemeType: string;
                },
            ) => {
                if (!hasOwnershipScheme(ownershipTypeDetails)) return null;
                return isOtherOwnershipSchemeValue(
                    ownershipTypeDetails.ownershipSchemeType,
                )
                    ? ownershipTypeDetails.otherOwnershipSchemeType
                    : value;
            },
        },
    },
    otherOwnershipSchemeType: {
        items: otherOwnershipSchemeTypeOptions,
        default: null,
        validation: Yup.string()
            .nullable()
            .when('ownershipSchemeType', {
                is: (value: string) => isOtherOwnershipSchemeValue(value),
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),
    },
    ownershipSchemeRepaid: {
        default: null,
        validation: Yup.string()
            .nullable()
            .when('hasSharedOwnership', {
                is: 'true',
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),
        transform: {
            target: 'ownershipSchemeRepaid',
            toRequest: (value: string, propertyDetails: any) =>
                hasOwnershipScheme(propertyDetails) ? value : null,
        },
    },
    estimatedValue: {
        validation: Yup.string()
            .required('Enter your estimated property value')
            .test(
                'minimumAmount',
                'Your property must be worth at least £100,000',
                (value) => {
                    if (!value) return true;
                    return normalizeNumber(value) >= minimumPropertyWorth;
                },
            ),
        transform: {
            target: 'estimatedValue',
            toRequest: (value: string) => {
                return normalizeNumber(value);
            },
        },
    },
    purchaseValue: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: {
            target: 'purchaseValue',
            toRequest: (value: string) => {
                return normalizeNumber(value);
            },
        },
    },
    propertyTypeBase: {
        validation: Yup.string().required(requiredErrorMessage),
    },
    propertyType: {
        validation: Yup.string().required(requiredErrorMessage),
        transform: {
            target: 'propertyType',
            toRequest: (value: string) =>
                value === 'End-of-terrace house' ? 'Terraced house' : value,
        },
    },
    constructionYear: {
        items: constructionYearOptions,
        default: constructionYearOptions,
        validation: Yup.string()
            .typeError(requiredErrorMessage)
            .required(requiredErrorMessage),
        transform: { target: 'constructionYear' },
    },
    hasBeenBuiltInThePast2Years: {
        ...yesNo,
        validation: Yup.boolean()
            .when('constructionYear', {
                is: (value: string) => wasConstructedAfter2010(value),
                then: Yup.boolean()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            })
            .nullable(),
        transform: {
            target: 'hasBeenBuiltInThePast2Years',
            toRequest: (value: any, propertyDetails: any) =>
                wasConstructedAfter2010(propertyDetails.constructionYear)
                    ? value
                    : undefined,
        },
    },
    warrantyCertificate: {
        items: warrantyCertificateOptions,
        default: warrantyCertificateOptions,
        validation: Yup.string()
            .nullable()
            .when('hasBeenBuiltInThePast2Years', {
                is: true,
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),
        transform: {
            target: 'buildingWarrantyCertificate',
            toRequest: (value: any, propertyDetails: any) =>
                wasConstructedAfter2010(propertyDetails.constructionYear)
                    ? value
                    : undefined,
        },
    },
    tenure: {
        validation: Yup.string().required(requiredErrorMessage),
        transform: {
            target: 'tenure',
            toRequest: (value: string) =>
                value === 'true' ? 'Leasehold' : null,
            fromRequest: (value: string) => {
                if (value === 'Leasehold') {
                    return 'true';
                } else if (value === null) {
                    return false;
                }
            },
        },
    },
    yearsRemainingOnLease: {
        validation: Yup.string()
            .nullable()
            .when('tenure', {
                is: 'true',
                then: Yup.string().required(requiredErrorMessage),
            }),
        transform: {
            target: 'yearsRemainingOnLease',
            fromRequest: (value: string | null) =>
                value ? parseInt(value) : undefined,
            toRequest: (value: any, propertyDetails: any) =>
                propertyDetails.tenure === 'true' && value ? value : null,
        },
    },
    isThePropertyAnExLocalAuthority: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: { target: 'isThePropertyAnExLocalAuthority' },
    },
    isListedAsGradeIOrGradeIiOrA: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: { target: 'isListedAsGradeIOrGradeIiOrA' },
    },
    notOfStandardConstruction: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: { target: 'notOfStandardConstruction' },
    },
    notResidentialUse: {
        validation: Yup.string().nullable().required(requiredErrorMessage),
        transform: { target: 'notResidentialUse' },
    },
    hasPropertyValueAffectingFactors: {
        default: null,
        validation: Yup.string()
            .nullable()
            .when(htbConditionFields, {
                is: isHelpToBuyNotRepaid,
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),
        transform: {
            target: 'hasPropertyValueAffectingFactors',
            toRequest: htbToRequest(),
            fromRequest: booleanFromRequest,
        },
    },
    impactToPropertyValue: {
        default: impactToPropertyValueOptions,
        validation: Yup.array()
            .nullable()
            .when(['hasPropertyValueAffectingFactors', ...htbConditionFields], {
                is: (
                    hasFactors: string,
                    ownershipSchemeType: string,
                    ownershipSchemeRepaid: string,
                ) =>
                    hasFactors === 'true' &&
                    isHelpToBuyNotRepaid(
                        ownershipSchemeType,
                        ownershipSchemeRepaid,
                    ),
                then: Yup.array()
                    .test(
                        'selected',
                        "Select any issues that may affect your property's value",
                        (options: any) => {
                            if (!options) return false;
                            return options.some((opt: any) => opt.checked);
                        },
                    )
                    .test(
                        'noExternalCladding',
                        externalCladdingHardStopMessage,
                        (options: any) => {
                            if (!options) return true;
                            const cladding = options.find(
                                (opt: any) => opt.value === 'External cladding',
                            );
                            return !cladding?.checked;
                        },
                    ),
            }),
        transform: {
            target: 'impactToPropertyValue',
            toRequest: htbToRequest((value: any[]) => {
                if (!value) return null;
                const selected = value
                    .filter((opt: any) => opt.checked)
                    .map((opt: any) => opt.value);
                return selected.length ? selected.join(', ') : null;
            }),
            fromRequest: (value: string) => {
                if (!value) return impactToPropertyValueOptions;
                const selected = value.split(', ');
                return impactToPropertyValueOptions.map((opt) => ({
                    ...opt,
                    checked: selected.includes(opt.value),
                }));
            },
        },
    },
    impactToPropertyValueOther: {
        default: '',
        validation: Yup.string()
            .nullable()
            .when(['impactToPropertyValue', ...htbConditionFields], {
                is: (
                    options: any[],
                    ownershipSchemeType: string,
                    ownershipSchemeRepaid: string,
                ) =>
                    isHelpToBuyNotRepaid(
                        ownershipSchemeType,
                        ownershipSchemeRepaid,
                    ) &&
                    options?.some(
                        (opt) => opt.value === 'Other' && opt.checked,
                    ),
                then: Yup.string()
                    .typeError(
                        "Describe the other issues that may affect your property's value",
                    )
                    .required(
                        "Describe the other issues that may affect your property's value",
                    ),
            }),
        transform: {
            target: 'impactToPropertyValueOther',
            toRequest: htbToRequest((value: string, propertyDetails: any) => {
                const options = propertyDetails.impactToPropertyValue;
                const otherChecked = options?.some(
                    (opt: any) => opt.value === 'Other' && opt.checked,
                );
                return otherChecked ? value : null;
            }),
        },
    },
    hasStructuralAlterationsToProperty: {
        default: null,
        validation: Yup.string()
            .nullable()
            .when(htbConditionFields, {
                is: isHelpToBuyNotRepaid,
                then: Yup.string()
                    .typeError(requiredErrorMessage)
                    .required(requiredErrorMessage),
            }),
        transform: {
            target: 'structuralAlterationsToProperty',
            toRequest: htbToRequest(),
            fromRequest: booleanFromRequest,
        },
    },
    structuralAlterationsApprovedByHomesEngland: {
        default: null,
        validation: Yup.string()
            .nullable()
            .when(
                ['hasStructuralAlterationsToProperty', ...htbConditionFields],
                {
                    is: hasHtbStructuralAlterations,
                    then: Yup.string()
                        .typeError(requiredErrorMessage)
                        .required(requiredErrorMessage),
                },
            ),
        transform: {
            target: 'structuralAlterationsApprovedByHomesEng',
            toRequest: htbToRequest((value: string, propertyDetails: any) =>
                propertyDetails.hasStructuralAlterationsToProperty === 'true'
                    ? value
                    : null,
            ),
            fromRequest: booleanFromRequest,
        },
    },
    structuralAlterationsDetails: {
        default: '',
        validation: Yup.string()
            .nullable()
            .when(
                ['hasStructuralAlterationsToProperty', ...htbConditionFields],
                {
                    is: hasHtbStructuralAlterations,
                    then: Yup.string()
                        .typeError(
                            'Describe the structural alterations made to your property',
                        )
                        .required(
                            'Describe the structural alterations made to your property',
                        ),
                },
            ),
        transform: {
            target: 'structuralAlterationsToPropertyDetails',
            toRequest: htbToRequest((value: string, propertyDetails: any) =>
                propertyDetails.hasStructuralAlterationsToProperty === 'true'
                    ? value
                    : null,
            ),
        },
    },
    helpToBuyEquityLoanAccountNumber: {
        default: null,
        validation: Yup.string()
            .nullable()
            .when(htbConditionFields, {
                is: isHelpToBuyNotRepaid,
                then: Yup.string()
                    .typeError(
                        'Enter your Help to Buy equity loan account number',
                    )
                    .required(
                        'Enter your Help to Buy equity loan account number',
                    ),
            }),
        transform: {
            target: 'htbEquityLoanAccountNumber',
            toRequest: htbToRequest(),
        },
    },
    floorOfFlat: {
        validation: Yup.string()
            .nullable()
            .when('propertyType', {
                is: (value: string) => isFlat(value),
                then: Yup.string().nullable().required(requiredErrorMessage),
            }),
        transform: {
            target: 'floorOfFlat',
            toRequest: (value: any, propertyDetails: any) =>
                isFlat(propertyDetails.propertyType) ? value : undefined,
        },
    },
    is4thFloorOrHigherWithoutLift: {
        validation: Yup.string()
            .nullable()
            .when('floorOfFlat', {
                is: (value: string) => value === '4',
                then: Yup.string().nullable().required(requiredErrorMessage),
            }),
        transform: {
            target: 'is4thFloorOrHigherWithoutLift',
            toRequest: (value: any) => (value ? value : undefined),
        },
    },
    numberOfBedrooms: {
        validation: Yup.number()
            .nullable()
            .required(numberOfBedroomsErrorMessage),
        transform: {
            target: 'numberOfBedrooms',
            toRequest: (value: string) => {
                return normalizeNumber(value);
            },
        },
    },
};
